import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Pencil, Person, PersonAdd } from 'react-bootstrap-icons'
import { Link, useNavigate } from 'react-router-dom'
import useDebounce from '../../../../components/Hooks/useDebounce'
import Authentication from '../../../../libs/api/services/Authentication'
import Patient from '../../../../libs/api/services/Patient'
import Team from '../../../../libs/api/services/Team'
import User from '../../../../libs/api/services/User'
import { ClaimsFilter } from '../../../../libs/authentication/ClaimsFilter'
import CommonFunctionsContext from '../../../common/CommonFunctionsProvider'

export default function PatientManagementScreen() {
    const [loading, setLoading] = useState(true)
    const [patientsSearch, setPatientsSearch] = useState([])
    const [patients, setPatients] = useState([])
    const { createModal } = useContext(CommonFunctionsContext)

    useEffect(() => {
        const load = async () => { setPatients(await Patient.ListPatients()); setLoading(false) }
        load()
    }, [])

    useEffect(() => {
        setPatientsSearch(patients)
    }, [patients])

    const profileValue = (patient, keys = []) => {
        const profiles = patient?.profiles || []
        const normalizedKeys = keys.map(key => key.toUpperCase())
        const entry = profiles.find(profile => normalizedKeys.includes((profile?.detailsType?.profileDetailKey || '').toUpperCase()))
        return entry?.detail || ''
    }

    const getPatientNames = (patient) => {
        const firstName = profileValue(patient, ['FIRST_NAME']) || patient?.userProfile?.firstName || ''
        const lastName = profileValue(patient, ['LAST_NAME']) || patient?.userProfile?.lastName || ''
        return { firstName, lastName }
    }

    const handleSearch = (e) => {
        const rawQuery = e.target.value ?? ''
        const query = rawQuery.trim().toLowerCase()

        if (query === '') return setPatientsSearch(patients)

        const queryParts = query.split(/\s+/).filter(Boolean)
        const results = patients.filter(patient => {
            const { firstName, lastName } = getPatientNames(patient)
            const fullName = `${firstName} ${lastName}`.toLowerCase()
            const profileDetails = (patient?.profiles || []).map(profile => profile?.detail || '').join(' ').toLowerCase()
            const searchableText = `${fullName} ${profileDetails}`

            return queryParts.every(part => searchableText.includes(part))
        })

        setPatientsSearch(results)
    }

    const _handleNewPatient = (e) => {
        createModal(<NewUserDetails />, "New Patient", <PersonAdd />)
    }
    return (loading ?
        <div className='d-flex flex-grow-1 flex-column align-items-center justify-content-center'>
            <FontAwesomeIcon icon={faSpinner} size={'4x'} spin />
            <b className='p-3'>Loading Patients</b>
        </div> :
        <div className='d-flex flex-column h-100 m-3 flex-grow-1' style={{ overflow: 'auto' }}>
            <h1 className='text-center'>Patients</h1>
            <div className='d-flex'>
                <div className='flex-grow-1'>
                    <input onChange={handleSearch} className='form-control' placeholder='Search Patients' />
                </div>
                <ClaimsFilter resources={[{ resource: "PatientProfile", type: 'Inherit-Edit' }, { resource: "PatientProfile", type: 'Edit' }]} claim={[]}>
                    <div className='ms-5'>
                        <button onClick={_handleNewPatient} className='btn bg-secondary-container'> <PersonAdd />Add Patient</button>
                    </div>
                </ClaimsFilter>
            </div>
            <div>
                {patientsSearch && patientsSearch.map(patient => {
                    const { firstName, lastName } = getPatientNames(patient)
                    return <Link to={`/Dashboard/patient/${patient.id}/Dashboard`} className='text-reset' key={patient.id}>
                        <div key={patient.id} style={{ cursor: 'pointer' }} className='d-flex p-2 align-items-center'>
                            <div><Person /></div>
                            {/* <div className='flex-grow-1'>{patient.profiles && patient.profiles.map(a => a.detail).join(' ')}</div> */}
                            <div className='flex-grow-1'>{firstName} {lastName}</div>
                            <div><Pencil /></div>
                        </div>
                    </Link>
                })}
            </div>
        </div>
    )
}


const NewUserDetails = ({ close }) => {
    const { showError } = useContext(CommonFunctionsContext)
    const nav = useNavigate()
    const [teams, setTeams] = useState()
    const [profile, setProfile] = useState({
        idNumber: '',
        firstName: '',
        lastName: '',
        mobileNumber: '',
        emailAddress: '',
    })
    const [status, setStatus] = useState()
    // const [idNumber, setIDNumber] = useState()
    const debouncedInputValue = useDebounce(profile.idNumber, 300);
    const IDNumberRef = useRef()
    const TeamRef = useRef()

    useEffect(() => {
        if (debouncedInputValue && debouncedInputValue.length > 12) {
            setStatus('Searching for user profile')
            User.LookupByIDNumber(debouncedInputValue)
                .then(res => {
                    setTimeout(() => { setStatus() }, 1000)
                    if (res)
                        setProfile({ ...res, id: undefined })
                }).catch(e => setTimeout(() => { setStatus(); }, 1000))

        } else setStatus()
    }, [debouncedInputValue])

    const _handleSubmit = async (e) => {
        e.preventDefault()

        var onboard = await Patient.OnboardPatientProfile(Authentication.getFacility(), profile).catch(e => showError(e))
        if (TeamRef.current.value !== '' && TeamRef.current.value !== 'undefined')
            Team.AssignPatient(TeamRef.current.value, onboard.id)

        setStatus('Creating profile')
        close(() => nav(`Dashboard/patient/${onboard.id}`))
    }

    useEffect(() => { if (IDNumberRef.current) IDNumberRef.current.focus() }, [IDNumberRef])
    useEffect(() => {
        const load = async () => setTeams(await Team.GetFacilitiesTeams().catch(showError))
        load()
    }, [])

    return (<form onSubmit={_handleSubmit} className='d-flex flex-column flex-grow-1'>

        <div className='flex-grow-1 m-3'>
            <h2>Basic Information</h2>
            <div>
                <label>ID Number</label>
                <input ref={IDNumberRef} value={profile.idNumber}
                    onChange={e => setProfile({ ...profile, idNumber: e.target.value })}
                    className='form-control' placeholder='ID Number' autofocus />
            </div>
            {status && <div className='text-center'>
                <><FontAwesomeIcon icon={faSpinner} spin /> {status}</>
            </div>}
            {
                profile.idNumber && profile.idNumber.length > 12 &&
                <>
                    <div className='d-flex'>
                        <div className='w-50 pe-2'>
                            <label>First Name</label>
                            <input
                                value={profile.firstName}
                                onChange={e => setProfile({ ...profile, firstName: e.target.value })}
                                className='form-control' placeholder='First Name' />
                        </div>
                        <div className='w-50 ps-2'>
                            <label>Last Names</label>
                            <input
                                value={profile.lastName}
                                onChange={e => setProfile({ ...profile, lastName: e.target.value })}
                                className='form-control' placeholder='Last Names' />
                        </div>
                    </div>
                    <div className='d-flex'>
                        <div className='w-50 pe-2'>
                            <label>Mobile</label>
                            <input
                                type='tel'
                                required
                                value={profile.mobileNumber}
                                onChange={e => setProfile({ ...profile, mobileNumber: e.target.value })}
                                className='form-control' placeholder='Mobile' />
                        </div>
                        <div className='w-50 ps-2'>
                            <label>Email</label>
                            <input
                                type='Email'
                                required
                                value={profile.emailAddress}
                                onChange={e => setProfile({ ...profile, emailAddress: e.target.value })}
                                className='form-control' placeholder='Email' />
                        </div>
                    </div>
                    <h2>Clinical Team</h2>
                    <div className='w-100' style={{ cursor: 'pointer' }}>
                        <select ref={TeamRef} name='teamID' className='form-control'>
                            <option></option>
                            {teams && teams.map(team => <option value={team.id} key={team.id}>{team.teamName}</option>)}
                        </select>
                    </div>
                </>
            }

        </div>
        <div className='text-center'><button type='submit' className='btn bg-primary-container'>Next</button></div>
    </form>
    )
}